import { KeyboardEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { RefreshCw, Search } from "lucide-react";
import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";
import { Pagination } from "@/shared/ui/pagination";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/ui/select";
import type { CsMessageListItem, CsSession, CsSessionDetail } from "../../api/aiKefuService";
import { SessionsTable } from "./SessionsTable";
import { SessionDetailCard } from "./SessionDetailCard";
import { KefuEmptyGuide } from "./KefuEmptyGuide";
import { CHANNEL_FILTER_OPTIONS } from "./constants";
import { OrgScopeFilterBar } from "@/entities/org-department/ui/OrgScopeFilterBar";
import type { OrgScopeFilter } from "@/shared/hooks/useOrgScopeFilter";

type Props = {
  sessions: CsSession[];
  total: number;
  page: number;
  pageSize: number;
  search: string;
  status: string;
  onSearchSubmit: (q: string) => void;
  onStatusChange: (status: string) => void;
  /** 1010 渠道筛选(all / wecom_kf / web_widget);不传 onChannelChange 则不显示筛选框。 */
  channel?: string;
  onChannelChange?: (channel: string) => void;
  onPageChange: (page: number) => void;
  isLoading: boolean;
  onRefresh: () => void;
  // #9 (2026-07-05) — 组织级归属筛选(共享单元,替代 MANAGER+「只看我的接管」toggle)
  orgFilter: OrgScopeFilter;

  selectedSessionId: string;
  onSelectSession: (id: string) => void;
  onCloseDetail: () => void;

  sessionDetail: CsSessionDetail | undefined;
  sessionMessages: CsMessageListItem[];
  onLoadEarlierMessages: () => void;
  hasEarlierMessages: boolean;
  isLoadingEarlierMessages: boolean;
  onRefreshDetail: () => void;

  takeoverPendingId?: string;
  closePendingId?: string;
  onTakeover?: (id: string, channel: string) => void;
  onCloseSession: (id: string) => void;

  replyInput: string;
  onReplyInputChange: (v: string) => void;
  onSendReply: () => void;
  onReplyKeyDown: (e: KeyboardEvent<HTMLInputElement>) => void;
  isReplying: boolean;
  markAsBenchmark: boolean;
  onMarkAsBenchmarkChange: (value: boolean) => void;
};

// Values must match backend cs_sessions.status enum exactly.
const STATUS_OPTION_KEYS: { value: string; labelKey: string }[] = [
  { value: "all", labelKey: "sessions.statusAll" },
  { value: "active", labelKey: "sessions.statusActive" },
  { value: "human_handling", labelKey: "sessions.statusHumanHandling" },
  { value: "closed", labelKey: "sessions.statusClosed" },
];

export function SessionsTab({
  sessions,
  total,
  page,
  pageSize,
  search,
  status,
  onSearchSubmit,
  onStatusChange,
  channel = "all",
  onChannelChange,
  onPageChange,
  isLoading,
  onRefresh,
  orgFilter,
  selectedSessionId,
  onSelectSession,
  onCloseDetail,
  sessionDetail,
  sessionMessages,
  onLoadEarlierMessages,
  hasEarlierMessages,
  isLoadingEarlierMessages,
  onRefreshDetail,
  takeoverPendingId,
  closePendingId,
  onTakeover,
  onCloseSession,
  replyInput,
  onReplyInputChange,
  onSendReply,
  onReplyKeyDown,
  isReplying,
  markAsBenchmark,
  onMarkAsBenchmarkChange,
}: Props) {
  const { t } = useTranslation("customer-service");
  const [searchDraft, setSearchDraft] = useState(search);
  useEffect(() => setSearchDraft(search), [search]);

  const isFirstRunEmpty =
    !isLoading &&
    total === 0 &&
    sessions.length === 0 &&
    !search &&
    status === "all" &&
    channel === "all";

  const submitSearch = () => {
    const trimmed = searchDraft.trim();
    if (trimmed !== search) onSearchSubmit(trimmed);
  };
  const handleSearchKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      submitSearch();
    }
  };

  return (
    <div className="space-y-4 mt-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={t("sessions.searchPlaceholder")}
              value={searchDraft}
              onChange={(e) => setSearchDraft(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              onBlur={submitSearch}
              className="pl-9 w-64"
            />
          </div>
          <Select value={status} onValueChange={onStatusChange}>
            <SelectTrigger className="w-[140px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTION_KEYS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {t(opt.labelKey)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {onChannelChange && (
            <Select value={channel} onValueChange={onChannelChange}>
              <SelectTrigger className="w-[140px]" aria-label={t("sessionsTable.headerChannel")}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CHANNEL_FILTER_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {t(opt.labelKey)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {/* #9/B (2026-07-06) — 组织级归属筛选(共享树 picker,替代 MANAGER+「只看我的接管」toggle)。
              归入左侧筛选组(与搜索/状态并排),刷新动作独立在右,消除旧版浮在工具行中间的观感。 */}
          <OrgScopeFilterBar filter={orgFilter} />
        </div>
        <Button variant="outline" size="sm" onClick={onRefresh}>
          <RefreshCw className="h-4 w-4 mr-1" />
          {t("sessions.refresh")}
        </Button>
      </div>

      {/* 1004 T7:一条会话都没有(且没在筛)→ 引导卡替掉空表 */}
      {isFirstRunEmpty ? (
        <KefuEmptyGuide onRefresh={onRefresh} />
      ) : (
        <SessionsTable
          sessions={sessions}
          isLoading={isLoading}
          takeoverPendingId={takeoverPendingId}
          closePendingId={closePendingId}
          onSelectSession={onSelectSession}
          onTakeover={onTakeover}
          onCloseSession={onCloseSession}
        />
      )}

      {total === 0 ? (
        isFirstRunEmpty ? null : (
          <p className="text-xs text-muted-foreground">{t("sessions.empty")}</p>
        )
      ) : (
        <Pagination
          page={page - 1}
          pageSize={pageSize}
          total={total}
          disabled={isLoading}
          onPageChange={(p) => onPageChange(p + 1)}
        />
      )}

      {selectedSessionId && (
        <SessionDetailCard
          sessionId={selectedSessionId}
          detail={sessionDetail}
          messages={sessionMessages}
          onLoadEarlier={onLoadEarlierMessages}
          hasEarlier={hasEarlierMessages}
          isLoadingEarlier={isLoadingEarlierMessages}
          onClose={onCloseDetail}
          onRefresh={onRefreshDetail}
          replyInput={replyInput}
          onReplyInputChange={onReplyInputChange}
          onSendReply={onSendReply}
          onReplyKeyDown={onReplyKeyDown}
          isReplying={isReplying}
          markAsBenchmark={markAsBenchmark}
          onMarkAsBenchmarkChange={onMarkAsBenchmarkChange}
        />
      )}
    </div>
  );
}
